// The Caravan Forms renderer. Framework-free; DOM access only inside function
// bodies so the module imports cleanly under node (SSR/test bundles).
// Styling: every element carries a stable cn-f-<slot> hook plus the optional
// site-supplied class from opts.classMap (data-cn-class-* on the container) —
// the site's Tailwind scanner keeps utilities because they live in ITS markup.
// Contract details (submit body, response shapes) mirror functions/src/forms.js
// and formsPipeline.js — see the plan's Global Constraints for the exact table.
import { evaluateShowIf } from './conditions.js'

// Mirrors functions/src/lib/normalize.js EMAIL_RE (client-side UX only; the
// server revalidates authoritatively).
const EMAIL_RE = /^[a-z0-9][a-z0-9._%+-]*@[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/
const URL_RE = /^https?:\/\/\S+$/i
const FALLBACK_ERROR = 'Sorry, something went wrong. Please email us at hello@careernomad.org.'
const HONEYPOT_NAME = 'website__extra'

export function classFor(map, slot) {
  const extra = map && typeof map[slot] === 'string' ? map[slot].trim() : ''
  return extra ? `cn-f-${slot} ${extra}` : `cn-f-${slot}`
}

/** Optional post-submit CTA. `success.message` is rendered as textContent, so
 * a link cannot be smuggled in as markup — it has to be its own field. Only
 * http(s) qualifies, mirroring the guard on success.redirectUrl, so a bad or
 * `javascript:` value in a definition can never become a live link. */
export function successLink(success) {
  const url = success && typeof success.linkUrl === 'string' ? success.linkUrl.trim() : ''
  if (!URL_RE.test(url)) return null
  const label = success && typeof success.linkLabel === 'string' ? success.linkLabel.trim() : ''
  return { url, label: label || 'Book a call' }
}

/** crypto.randomUUID() is unavailable in some older/locked-down embed
 * contexts (non-secure contexts, older browsers); this fallback keeps sid
 * minting working everywhere without pulling in a uuid dependency. */
export function makeSid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  let s = ''
  const hex = '0123456789abcdef'
  for (let i = 0; i < 32; i++) s += hex[Math.floor(Math.random() * 16)] + (i === 7 || i === 11 || i === 15 || i === 19 ? '-' : '')
  return s
}

/** Split the server's per-field 400 errors ("<key> is …") from general ones. */
export function fieldErrorsFrom(errors, keys) {
  const byKey = {}
  const general = []
  for (const e of Array.isArray(errors) ? errors : []) {
    const key = keys.find((k) => e === k || e.startsWith(k + ' '))
    if (key && !(key in byKey)) byKey[key] = e
    else if (!key) general.push(e)
  }
  return { byKey, general }
}

// ── Turnstile timing ─────────────────────────────────────────────────────────
// The old flow polled 3s for a token and then submitted regardless. An empty
// token is quarantined server-side while the visitor is shown the success
// message, so every slow or blocked challenge silently destroyed a real lead
// (measured at 1 in 5 on production, 2026-08-31). Nothing here submits without
// a token any more; a slow challenge now delays, and a broken one now says so.

/** How long a submit click will wait for a token before giving up and telling
 * the visitor. Generous on purpose: an interactive challenge under load takes
 * far longer than the old 3s, and waiting is always better than discarding. */
export const TURNSTILE_WAIT_MS = 20000

/** How long to wait for challenges.cloudflare.com itself. A blocked request
 * usually errors fast, but strict proxies black-hole it instead of failing, so
 * the script load needs its own ceiling or mountTurnstile never settles. */
export const TURNSTILE_LOAD_TIMEOUT_MS = 10000

/** Cloudflare expires tokens after 300s. Treat them as stale earlier so a
 * visitor who takes their time on a long form (/speaking, /signalaudit) gets a
 * fresh token instead of a `timeout-or-duplicate` rejection they never see. */
export const TURNSTILE_TOKEN_TTL_MS = 240000

export const TURNSTILE_BLOCKED_MSG = "We couldn't load the spam-protection check, which usually means a browser extension, proxy, or privacy setting is blocking challenges.cloudflare.com. Allow it and reload, or email us at hello@careernomad.org and we'll pick it up from there."

export const TURNSTILE_PENDING_MSG = "The spam-protection check hasn't finished yet. Wait a moment and press submit again, or email us at hello@careernomad.org if it keeps failing."

const TURNSTILE_VERIFYING_LABEL = 'Verifying…'
const TURNSTILE_VERIFYING_MSG = "Checking you're human — one moment."

/** A token older than the TTL will be rejected by siteverify, so it must be
 * refreshed rather than sent. No timestamp means we can't vouch for it. */
export function tokenIsStale(tokenAt, now, ttlMs = TURNSTILE_TOKEN_TTL_MS) {
  if (!tokenAt) return true
  return now - tokenAt > ttlMs
}

/** The submit decision, kept pure so it is unit-tested rather than reasoned
 * about. `status` is one of 'off' | 'pending' | 'ready' | 'unavailable'.
 * Returns 'submit', 'wait' (token still coming), or 'block' (+ message) —
 * never "submit anyway". */
export function turnstileGate({ required, status, token, tokenAt, now }) {
  if (!required || status === 'off') return { action: 'submit' }
  if (status === 'unavailable') return { action: 'block', message: TURNSTILE_BLOCKED_MSG }
  if (token && !tokenIsStale(tokenAt, now)) return { action: 'submit' }
  return { action: 'wait' }
}

/** Poll `read()` until it yields a token, `shouldAbort()` says the wait is
 * pointless, or the budget runs out. Clock and sleep are injected so tests run
 * instantly with a fake clock. */
export async function pollForToken({
  read,
  shouldAbort = () => false,
  now = () => Date.now(),
  sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  timeoutMs = TURNSTILE_WAIT_MS,
  stepMs = 150,
}) {
  const start = now()
  let token = read()
  if (token) return token
  while (now() - start < timeoutMs) {
    // The widget can go from 'pending' to 'unavailable' mid-wait — a submit
    // that fires before mountTurnstile settles is the common case. Bail as
    // soon as that is known instead of staring at the full budget.
    if (shouldAbort()) return ''
    // eslint-disable-next-line no-await-in-loop
    await sleep(stepMs)
    token = read()
    if (token) return token
  }
  return ''
}

function withTimeout(promise, ms) {
  return new Promise((resolve) => {
    let settled = false
    const done = () => { if (!settled) { settled = true; resolve() } }
    const timer = setTimeout(done, ms)
    promise.then(() => { clearTimeout(timer); done() }, () => { clearTimeout(timer); done() })
  })
}

/** Serialises Turnstile script loading across every embed on a page.
 *
 * A truthy `window.turnstile` is NOT proof the API is usable: api.js assigns
 * the global before it calls the onload hook, so a second embed booting inside
 * that window would short-circuit on the global and render against a
 * half-initialised API — a widget that is created but never issues a token.
 * Once a load is in flight, every caller waits for the hook instead. The one
 * global read that stays is for an API the host page loaded before we ran,
 * where there is no hook of ours to wait on.
 *
 * Dependencies are injected so the ordering is testable without a DOM. */
export function createTurnstileLoader({ getApi, inject, callbackName = '__cnfTurnstileReady' }) {
  let loading = null
  let ready = false
  return function loadTurnstile() {
    if (ready) return Promise.resolve()
    if (loading) return loading
    if (getApi()) { ready = true; return Promise.resolve() }
    loading = new Promise((resolve) => {
      inject({
        callbackName,
        // Only the hook proves readiness; re-read the global through it so a
        // script that fired onload without exposing the API stays not-ready.
        onReady: () => { ready = Boolean(getApi()); resolve() },
        // Blocked or black-holed: settle so mountTurnstile can record
        // 'unavailable' rather than leaving every embed hanging.
        onError: () => resolve(),
      })
    })
    return loading
  }
}

const loadTurnstile = createTurnstileLoader({
  getApi: () => window.turnstile,
  inject: ({ callbackName, onReady, onError }) => {
    window[callbackName] = onReady
    const s = document.createElement('script')
    s.src = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${callbackName}`
    s.defer = true
    s.onerror = onError
    document.head.appendChild(s)
  },
})

/** Public handle: each update() tears down the old generation and renders a
 * fresh one — the caller's handle stays valid across generations. */
export function renderForm(container, def, opts) {
  let inner = renderOnce(container, def, opts)
  return {
    update(nextDef) {
      inner.destroy()
      inner = renderOnce(container, nextDef, opts)
    },
    destroy() { inner.destroy() },
  }
}

let instanceCounter = 0

function renderOnce(container, def, opts) {
  const { mode, endpoint, classMap = {} } = opts
  const d = def.definition
  const slug = def.slug
  const instance = ++instanceCounter // a11y ids stay unique with multiple instances of one form per page
  const doc = container.ownerDocument
  const turnstileRequired = mode === 'live' && Boolean(def.turnstileSiteKey)
  const state = {
    sid: makeSid(),
    renderedAt: Date.now(),
    turnstileToken: '',
    turnstileTokenAt: null,
    // 'off' | 'pending' | 'ready' | 'unavailable' — drives turnstileGate().
    turnstileStatus: turnstileRequired ? 'pending' : 'off',
    turnstileWidgetId: null,
    inFlight: false,
    destroyed: false,
  }

  const el = (tag, slot, attrs = {}) => {
    const node = doc.createElement(tag)
    node.className = classFor(classMap, slot)
    for (const [k, v] of Object.entries(attrs)) {
      if (v === true) node.setAttribute(k, '')
      else if (v !== false && v != null) node.setAttribute(k, String(v))
    }
    return node
  }
  const fieldId = (key) => `cnf-${slug}-${instance}-${key}`

  const form = el('form', 'form', { novalidate: true, 'data-cn-form': '' })
  const rows = [] // { field, wrapper, input, errorNode }
  const currentValues = () => {
    const values = {}
    for (const r of rows) {
      const v = (r.input.value || '').trim()
      if (v !== '') values[r.field.key] = v
    }
    return values
  }
  const visibleRows = () => {
    const values = currentValues()
    return rows.filter((r) => evaluateShowIf(r.field.showIf, values))
  }

  // Build fields; consecutive half:true fields pair into a grid row.
  let pendingHalf = null
  for (const field of d.fields) {
    if (field.type === 'hidden') {
      const input = el('input', 'field', { type: 'hidden', name: field.key })
      rows.push({ field, wrapper: input, input, errorNode: null })
      form.appendChild(input)
      continue
    }
    const wrapper = el('div', 'field')
    const label = el('label', 'label', { for: fieldId(field.key) })
    label.textContent = field.label
    let input
    if (field.type === 'select') {
      input = el('select', 'select', { id: fieldId(field.key), name: field.key })
      const ph = doc.createElement('option')
      ph.value = ''
      ph.textContent = field.placeholder || field.label
      ph.disabled = true
      ph.selected = true
      input.appendChild(ph)
      for (const o of field.options || []) {
        const opt = doc.createElement('option')
        opt.value = o.value
        opt.textContent = o.label
        input.appendChild(opt)
      }
    } else if (field.type === 'textarea') {
      input = el('textarea', 'textarea', { id: fieldId(field.key), name: field.key, rows: 5, placeholder: field.placeholder || '' })
    } else {
      input = el('input', 'input', { id: fieldId(field.key), name: field.key, type: field.type, placeholder: field.placeholder || '' })
    }
    if (field.required) input.setAttribute('aria-required', 'true')
    const errorNode = el('p', 'error', { id: `${fieldId(field.key)}-err`, role: 'alert', hidden: true })
    wrapper.appendChild(label)
    wrapper.appendChild(input)
    wrapper.appendChild(errorNode)
    rows.push({ field, wrapper, input, errorNode })

    if (field.half && pendingHalf) {
      const row = el('div', 'row')
      row.appendChild(pendingHalf)
      row.appendChild(wrapper)
      form.appendChild(row)
      pendingHalf = null
    } else if (field.half) {
      pendingHalf = wrapper
    } else {
      if (pendingHalf) { form.appendChild(pendingHalf); pendingHalf = null }
      form.appendChild(wrapper)
    }
  }
  if (pendingHalf) form.appendChild(pendingHalf)

  // Honeypot (off-screen, out of tab order and the a11y tree).
  const hpWrap = doc.createElement('div')
  hpWrap.style.cssText = 'position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden'
  hpWrap.setAttribute('aria-hidden', 'true')
  const hpInput = doc.createElement('input')
  hpInput.type = 'text'
  hpInput.name = HONEYPOT_NAME
  hpInput.tabIndex = -1
  hpInput.setAttribute('aria-hidden', 'true')
  hpInput.setAttribute('autocomplete', 'off')
  hpWrap.appendChild(hpInput)
  form.appendChild(hpWrap)

  // Turnstile mount point (zero-height is fine for Invisible/Managed widgets).
  const tsMount = doc.createElement('div')
  form.appendChild(tsMount)

  const statusNode = el('p', 'status', { role: 'status', hidden: true })
  const button = el('button', 'button', { type: 'submit' })
  button.textContent = d.submitLabel
  form.appendChild(button)
  form.appendChild(statusNode)

  const live = el('div', 'success', { 'aria-live': 'polite' })
  container.textContent = ''
  container.appendChild(live)
  live.appendChild(form)

  function applyVisibility() {
    const values = currentValues()
    for (const r of rows) {
      if (r.field.type === 'hidden') continue
      // Toggle the field wrapper itself — in the seeds only always-visible
      // name fields are half-paired, so a row never hides as a unit.
      if (evaluateShowIf(r.field.showIf, values)) r.wrapper.removeAttribute('hidden')
      else r.wrapper.setAttribute('hidden', '')
    }
  }
  form.addEventListener('input', applyVisibility)
  form.addEventListener('change', applyVisibility)
  applyVisibility()

  function clientValidate() {
    const errors = []
    for (const r of visibleRows()) {
      const f = r.field
      if (f.type === 'hidden') continue
      const v = (r.input.value || '').trim()
      let msg = ''
      if (f.required && !v) msg = `${f.label} is required`
      else if (v && f.type === 'email' && !EMAIL_RE.test(v.toLowerCase())) msg = 'Please enter a valid email'
      else if (v && f.type === 'url' && !URL_RE.test(v)) msg = 'Please enter a valid link (https://…)'
      if (msg) errors.push({ row: r, msg })
    }
    return errors
  }

  function showFieldError(r, msg) {
    r.errorNode.textContent = msg
    r.errorNode.removeAttribute('hidden')
    r.input.setAttribute('aria-invalid', 'true')
    r.input.setAttribute('aria-describedby', r.errorNode.id)
  }
  function clearErrors() {
    statusNode.setAttribute('hidden', '')
    statusNode.textContent = ''
    for (const r of rows) {
      if (!r.errorNode) continue
      r.errorNode.setAttribute('hidden', '')
      r.input.removeAttribute('aria-invalid')
      r.input.removeAttribute('aria-describedby')
    }
  }
  function showStatus(msg) {
    statusNode.textContent = msg
    statusNode.removeAttribute('hidden')
  }

  function succeed() {
    if (d.success.mode === 'redirect' && mode === 'live' && d.success.redirectUrl && /^https?:\/\//i.test(d.success.redirectUrl)) {
      window.location.assign(d.success.redirectUrl)
      return
    }
    const msg = el('div', 'success')
    msg.textContent = d.success.message
    live.textContent = ''
    live.appendChild(msg)
    const cta = successLink(d.success)
    if (cta) {
      // Reuses the button slot so the site's existing data-cn-class-button
      // styling applies — no new per-page attribute needed for the CTA to
      // land on-brand. Appended as a sibling so it sits below the copy
      // rather than running on inline after it.
      const link = el('a', 'button', { href: cta.url, target: '_blank', rel: 'noopener' })
      link.textContent = cta.label
      live.appendChild(link)
    }
  }

  function clearToken() {
    state.turnstileToken = ''
    state.turnstileTokenAt = null
  }

  async function mountTurnstile() {
    if (!turnstileRequired) { state.turnstileStatus = 'off'; return }
    await withTimeout(loadTurnstile(), TURNSTILE_LOAD_TIMEOUT_MS)
    if (state.destroyed) return
    // Script blocked, black-holed, or loaded without exposing the API: the
    // widget will never issue a token, so record that instead of pretending.
    if (!window.turnstile) { state.turnstileStatus = 'unavailable'; return }
    try {
      state.turnstileWidgetId = window.turnstile.render(tsMount, {
        sitekey: def.turnstileSiteKey,
        callback: (token) => {
          state.turnstileToken = token
          state.turnstileTokenAt = Date.now()
        },
        // Transient challenge errors (Turnstile retries on its own): drop the
        // token and stay 'ready' so a submit waits rather than hard-failing.
        'error-callback': () => { clearToken() },
        'expired-callback': () => {
          clearToken()
          if (state.turnstileWidgetId != null) window.turnstile.reset(state.turnstileWidgetId)
        },
      })
      state.turnstileStatus = 'ready'
    } catch (err) {
      // A CSP frame-src violation or a hostile sandbox can throw here.
      console.error('caravan-forms: turnstile render failed', err)
      state.turnstileStatus = 'unavailable'
    }
  }
  mountTurnstile()

  /** Latest token, reading through to the widget in case the callback was
   * missed (a re-mounted widget can already hold a solved response). */
  function readTurnstileToken() {
    if (state.turnstileToken) return state.turnstileToken
    try {
      if (state.turnstileWidgetId != null && window.turnstile) {
        const resp = window.turnstile.getResponse(state.turnstileWidgetId) || ''
        if (resp) {
          state.turnstileToken = resp
          state.turnstileTokenAt = Date.now()
          return resp
        }
      }
    } catch { /* widget not ready yet */ }
    return ''
  }

  /** Resolve the gate, waiting visibly if the token is still coming.
   * Returns '' to proceed, or a message to show instead of submitting. */
  async function resolveTurnstile() {
    let gate = turnstileGate({
      required: turnstileRequired,
      status: state.turnstileStatus,
      token: state.turnstileToken,
      tokenAt: state.turnstileTokenAt,
      now: Date.now(),
    })
    if (gate.action !== 'wait') return gate.action === 'block' ? gate.message : ''

    // A stale token would only earn a `timeout-or-duplicate` rejection — throw
    // it away and ask the widget for a fresh one before waiting on it.
    if (state.turnstileToken) {
      clearToken()
      if (state.turnstileWidgetId != null && window.turnstile) {
        try { window.turnstile.reset(state.turnstileWidgetId) } catch { /* already gone */ }
      }
    }

    button.textContent = TURNSTILE_VERIFYING_LABEL
    button.setAttribute('aria-busy', 'true')
    showStatus(TURNSTILE_VERIFYING_MSG)
    try {
      await pollForToken({
        read: readTurnstileToken,
        shouldAbort: () => state.turnstileStatus === 'unavailable' || state.destroyed,
      })
    } finally {
      button.textContent = d.submitLabel
      button.removeAttribute('aria-busy')
      statusNode.setAttribute('hidden', '')
      statusNode.textContent = ''
    }
    if (state.destroyed) return ''

    gate = turnstileGate({
      required: turnstileRequired,
      status: state.turnstileStatus,
      token: state.turnstileToken,
      tokenAt: state.turnstileTokenAt,
      now: Date.now(),
    })
    if (gate.action === 'submit') return ''
    // Still nothing after the full budget. Say so — never submit empty.
    return gate.action === 'block' ? gate.message : TURNSTILE_PENDING_MSG
  }

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault()
    if (mode !== 'live' || state.inFlight) return
    clearErrors()
    const invalid = clientValidate()
    if (invalid.length) {
      for (const { row, msg } of invalid) showFieldError(row, msg)
      invalid[0].row.input.focus()
      return
    }
    state.inFlight = true
    button.setAttribute('disabled', '')
    let sent = false
    try {
      // Never submit without a live token. A slow challenge holds the button in
      // a visible "Verifying…" state; a blocked or stuck one shows an error the
      // visitor can act on. Both beat a false success and a lost lead.
      const blockedMsg = await resolveTurnstile()
      if (state.destroyed) return
      if (blockedMsg) { showStatus(blockedMsg); return }

      // Only visible values go on the wire (the server drops hidden ones anyway).
      const values = {}
      for (const r of visibleRows()) {
        const v = (r.input.value || '').trim()
        if (v !== '') values[r.field.key] = v
      }
      const base = {
        sid: state.sid,
        values,
        hp: hpInput.value || '',
        elapsedMs: Date.now() - state.renderedAt,
        turnstileToken: state.turnstileToken,
      }
      const body = opts.submitBody ? opts.submitBody(base) : base
      sent = true
      const resp = await fetch(`${endpoint}/submit/${slug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok && data.ok) { succeed(); return }
      if (resp.status === 400) {
        const keys = d.fields.map((f) => f.key)
        const { byKey, general } = fieldErrorsFrom(data.errors, keys)
        let first = null
        for (const r of rows) {
          if (r.errorNode && byKey[r.field.key]) {
            showFieldError(r, byKey[r.field.key])
            if (!first) first = r
          }
        }
        if (general.length) showStatus(general.join(' '))
        if (first) first.input.focus()
        state.sid = makeSid() // edited resubmission = a new submission
        return
      }
      showStatus(FALLBACK_ERROR)
    } catch {
      showStatus(FALLBACK_ERROR)
    } finally {
      state.inFlight = false
      button.removeAttribute('disabled')
      // Turnstile tokens are single-use — reset only when one was actually
      // spent. Resetting on a blocked submit would discard a challenge that is
      // still in flight and make the retry start from scratch. Guarded: a
      // successful submit swaps the form out, and reset on a gone widget throws.
      if (sent && state.turnstileWidgetId != null && window.turnstile) {
        clearToken()
        try { window.turnstile.reset(state.turnstileWidgetId) } catch { /* widget already removed */ }
      }
    }
  })

  container.dispatchEvent(new CustomEvent('cn-embed-rendered', { bubbles: true }))

  return {
    destroy() {
      state.destroyed = true // one-way per generation; the pending mountTurnstile await checks it
      if (state.turnstileWidgetId != null && window.turnstile) {
        try { window.turnstile.remove(state.turnstileWidgetId) } catch { /* already gone */ }
      }
      container.textContent = ''
    },
  }
}
